import "./style.css";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Toolbar from "./Toolbar/Toolbar";
import {
  AddConditionalBlock,
  ConditionalBlockNode,
  ConditionNode,
} from "./ConditionalBlock";
import { ImageNode } from "./Image";
import { MentionNode } from "./Mention/MentionNode";
import { VideoNode } from "./Video/VideoNode";

const USERS = [
  { id: "1", name: "John Doe", email: "john@example.com" },
  { id: "2", name: "Jane Smith", email: "jane@example.com" },
  { id: "3", name: "Alice Brown", email: "alice@example.com" },
];

export const TiptapTextEditor = ({
  value,
  handleChange,
  documentList = [],
  moduleType,
  documentTypes,
  handleDocumentChange,
  handleDocumentRemove,
  showCondBlock = false,
  isDisabledCondBlock = false,
  condBlockVarList = [],
  condBlockVarLoading = false,
  condBlockData = null,
  condBlockDataLoading = false,
  resetCondBlockData,
  showVariableDropdown = false,
  variableList = [],
  fetchOperatorsAndValue,
}) => {
  const [openCondBlockModal, setOpenCondBlockModal] = useState(false);
  const [editState, setEditState] = useState(null);

  const toggleCondBlockModal = () => {
    setOpenCondBlockModal(!openCondBlockModal);
  };

  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      MentionNode(USERS),
      ImageNode,
      VideoNode,
      ConditionalBlockNode,
      ConditionNode,
    ],
    content: "",
    onUpdate: ({ editor }) => {
      handleChange(editor.getHTML());
    },
    onEditConditionalBlock: ({ logic, conditions, pos }) => {
      setEditState({ logic, conditions, pos });
      toggleCondBlockModal();
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  return (
    <div>
      <AddConditionalBlock
        show={openCondBlockModal}
        toggle={() => {
          setEditState(null);
          toggleCondBlockModal();
        }}
        editor={editor}
        editState={editState}
        variableLoading={condBlockVarLoading}
        variableOptions={condBlockVarList}
        condBlockDataLoading={condBlockDataLoading}
        condBlockData={condBlockData}
        resetCondBlockData={resetCondBlockData}
        fetchOperatorsAndValue={fetchOperatorsAndValue}
        onSave={() => setEditState(null)}
      />

      {/* Toolbar */}
      <Toolbar
        editor={editor}
        showCondBlock={showCondBlock}
        isDisabledCondBlock={isDisabledCondBlock}
        toggleCondBlockModal={toggleCondBlockModal}
        showVariableDropdown={showVariableDropdown}
        variableList={variableList}
        moduleType={moduleType}
        documentTypes={documentTypes}
        handleDocumentChange={handleDocumentChange}
      />

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="p-2 rounded-bottom border border-top-0 min-h-250"
        placeholder="Type something..."
      />

      {documentList.length > 0 && (
        <div
          style={{
            marginTop: "12px",
            borderTop: "1px solid #eee",
            paddingTop: "8px",
          }}
        >
          {documentList.map((doc) => (
            <div
              key={doc.gid}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f9f9f9",
                padding: "6px 10px",
                borderRadius: "6px",
                marginBottom: "6px",
              }}
            >
              <div>
                📄{" "}
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  {doc.name}
                </a>{" "}
                ({doc.size})
              </div>
              <button
                onClick={() => handleDocumentRemove(doc.gid)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "red",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

TiptapTextEditor.propTypes = {
  value: PropTypes.string,
  handleChange: PropTypes.func,
  documentList: PropTypes.arrayOf(
    PropTypes.shape({
      gid: PropTypes.string,
      name: PropTypes.string,
      size: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  moduleType: PropTypes.string,
  documentTypes: PropTypes.array,
  handleDocumentChange: PropTypes.func,
  handleDocumentRemove: PropTypes.func,
  showCondBlock: PropTypes.bool,
  isDisabledCondBlock: PropTypes.bool,
  condBlockVarList: PropTypes.array,
  condBlockVarLoading: PropTypes.bool,
  condBlockData: PropTypes.object,
  condBlockDataLoading: PropTypes.bool,
  resetCondBlockData: PropTypes.func,
  showVariableDropdown: PropTypes.bool,
  variableList: PropTypes.array,
  fetchOperatorsAndValue: PropTypes.func,
};
